//
import { useEffect, useMemo } from 'react'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { AddAdmin, addAdmin, getAvailableEmployees } from 'core'

import { filterByKeyAndValue } from 'common/components/utils/SelectFilterUtils'

import { Button, Col, Icon, Notification, Row, DefaultSelectV2 as Select } from 'common/components'
import { useBreakPoints, useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

const formValidator = () =>
  Yup.object({
    id: Yup.string().required(''),
  })

interface Props {
  translate: (key: string) => string
  onAddAdmin?: () => void
}

const AddAdminForm = ({ translate, onAddAdmin }: Props) => {
  const { isXs } = useBreakPoints()

  const {
    data: availableEmployees,
    isLoading: availableEmployeesIsLoading,
    reload: reloadAvailableEmployees,
  } = useQueryRequestV2({
    initialState: [],
    queryFn: () => getAvailableEmployees(),
  })

  const { sendCommand: addAdminCommand, isLoading: addAdminIsLoading } = useCommandRequestV2(addAdmin)

  const submitHandler = async (values: AddAdmin) => {
    const res = await addAdminCommand(values)
    if (!res.hasErrors) {
      onAddAdmin && onAddAdmin()
      reloadAvailableEmployees()
      Notification.open({
        message: translate('admin-dashboard.company-settings.company-admins.add-admin-success-message'),
        type: 'success',
      })
    }
  }

  return (
    availableEmployees.length !== 0 && (
      <Formik
        enableReinitialize={true}
        validationSchema={formValidator()}
        initialValues={
          {
            id: '',
          } as AddAdmin
        }
        onSubmit={submitHandler}
      >
        {({ values, handleSubmit, handleBlur }) => (
          <form onSubmit={handleSubmit} autoComplete="off" onBlur={handleBlur}>
            <Row className="form__row">
              <Col xs={24} sm={10}>
                <Select
                  name="id"
                  items={availableEmployees}
                  disabled={availableEmployeesIsLoading}
                  loading={availableEmployeesIsLoading}
                  placeholder={translate(
                    'admin-dashboard.company-settings.company-admins.add-admin-employees-list-placeholder',
                  )}
                  filterOption={filterByKeyAndValue}
                />
              </Col>
              <Col className={`flex ${isXs ? 'justify-end' : ''}`} xs={24} sm={14}>
                <Button
                  htmlType="submit"
                  loading={addAdminIsLoading}
                  disabled={!formValidator().isValidSync(values)}
                  type="primary"
                  className="ml-24"
                >
                  <Icon type="far fa-plus" fontAwesome />
                  &nbsp;{translate('admin-dashboard.company-settings.company-admins.add-admin-button')}
                </Button>
              </Col>
            </Row>
          </form>
        )}
      </Formik>
    )
  )
}

export default AddAdminForm
